/* lp-set-switcher — <lp-set-switcher>: stage, then name + price → colour swatches → piece toggle (e.g. Shirt / Set /
   Shorts) → option pills (size) → quantity → add to cart. Pieces are separate products; price, stock, photo and the
   multi-buy offer all come from the selected piece. The two selectors cross out each other's gaps: a piece not made
   (or sold out) in the chosen colour is struck through; a colour the chosen piece isn't made (or is sold out) in is
   slashed. Both stay tappable: a crossed-out piece moves to its first available colour, a crossed-out colour moves to
   a piece that has it. No size is pre-picked; add to cart without one shakes the size row.
   Stage (data-stage): "photo" crossfades the variant photo; "cutouts" lays the colour's top cutout over its bottom
   cutout (Colour look blocks) and each piece shows top, bottom or both. A colour or piece missing a cutout it needs
   shows the photo.
   Motion, with GSAP (LP.gsap, loaded when the section nears the screen) and motion allowed:
   - colour: the ring glides to the swatch and the name rolls; the section tint sweeps out from the swatch in a
     soft-edged circle and the garments change colour as the edge passes them; the lit spot behind them crossfades.
     A colour picked elsewhere on the page (lp:colour-select) sweeps in once the section is on screen. On touch, a
     sideways swipe on the stage steps through the colours and the sweep comes in from that side.
   - piece: the thumb glides to the button; the garments move to their new places (Flip): the one that leaves
     drops or lifts away; title and price roll; the offer line folds open or shut; the size pills fade up.
   - first view: garments rise in, the floor shadow spreads, swatches pop. Idle: a slow float while on screen.
   Without GSAP, or with reduced motion, every change is instant (the ring and thumb still glide in CSS).
   Listens lp:colour-select {colour} and lp:product-change {handle}; emits lp:colour-select
   {colour, handle, source: 'lp-set-switcher'} when the shopper changes colour, and lp:selection {handle, title,
   label, url, colour, image, price} whenever the piece or colour shown changes (the sticky bar follows it). Cart add via /cart/add.js with
   bundled sections, then Impact's variant:add / cart:change (the drawer opens) and lp:cart-add.
   Gallery (data-lp-reel): the stage is slide one of a snap row; renderGallery adds the piece's photos in the chosen
   colour (data-lp-gallery) and page dots, and returns to slide one whenever piece or colour changes. No colour
   swipe on the stage in this mode.
   Size guide (openGuide): the piece's or the sock's Size chart dialog; unsold sizes dim, the picked size is marked.
   Order nudge + sock add-on (block `nudge`): cart subtotal (live, via cart:change) + this piece at its quantity is
   measured against the order-value tiers; the card says how far the next tier is when the biggest sock pack can
   close it (or confirms the tier already earned), and the switch adds the chosen sock variant × pack to the same
   /cart/add.js call. Sock prices show the tier the order lands in with them.
   GA4 ee_view_item_list in Analyzify id format on first view; never emits add_to_cart. */
(function () {
  'use strict';
  if (window.customElements.get('lp-set-switcher')) return;

  var FEATHER = 40; /* px: the soft edge of the sweep, the same 2.5rem as the tint gradient in the CSS */
  var slice = Array.prototype.slice;

  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function cssEsc(s) { return window.CSS && window.CSS.escape ? window.CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&'); }
  function money(cents, format, short) {
    var v = (cents / 100).toFixed(2);
    if (short || (format && format.indexOf('no_decimals') > -1)) v = v.replace(/\.00$/, '');
    if (!format) return '$' + v;
    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, v);
  }
  function norm(v) { return String(v == null ? '' : v).trim().toLowerCase(); }
  function isColour(n) { n = norm(n); return n === 'colour' || n === 'color'; }
  function isSize(n) { return norm(n) === 'size'; }
  function shortSize(v) { v = String(v || ''); return /^(Small|Medium|Large)$/.test(v) ? v.charAt(0) : v; }
  function parseTiers(str) {
    return String(str || '').split(',').map(function (p) { var a = p.split(':'); return { count: parseInt(a[0], 10), pct: parseFloat(a[1]) }; })
      .filter(function (t) { return t.count > 0 && t.pct > 0; }).sort(function (a, b) { return a.count - b.count; });
  }
  function gaPush(event, ecommerce, listId, listName) {
    var dl = (window.dataLayer = window.dataLayer || []);
    dl.push({ ecommerce: null });
    ecommerce.item_list_id = listId; ecommerce.item_list_name = listName;
    dl.push({ event: event, analyzify_source: 'lp-set-switcher', ecommerce: ecommerce });
  }
  function parse(el) { try { return JSON.parse(el ? el.textContent : '[]') || []; } catch (e) { return []; } }
  function motionOK() { return !!(window.LP && window.LP.motionOK); }
  /* the time fraction at which a power2.inOut tween reaches progress f (so a crossfade can meet the sweep's edge) */
  function inOut2At(f) { f = Math.max(0, Math.min(1, f)); return f < 0.5 ? Math.sqrt(f / 2) : 1 - Math.sqrt((1 - f) / 2); }
  /* only the mask's alpha counts: --s-text is always opaque (currentColor is not: the theme makes img text transparent) */
  function setMask(el, x, y, r) {
    var v = 'radial-gradient(circle at ' + x + 'px ' + y + 'px, var(--s-text) ' + Math.max(0, r - FEATHER) + 'px, transparent ' + r + 'px)';
    el.style.webkitMaskImage = v; el.style.maskImage = v;
  }
  function clearMask(el) { el.style.webkitMaskImage = ''; el.style.maskImage = ''; }
  function decoded(img) { return img && img.decode ? img.decode().catch(function () {}) : Promise.resolve(); }

  class LpSetSwitcher extends HTMLElement {
    connectedCallback() {
      this.pieces = parse(this.querySelector('[data-lp-pieces]'));
      if (!this.pieces.length) return;
      var self = this;
      this.format = this.dataset.moneyFormat || '';
      /* order nudge + sock add-on (block `nudge`) */
      this.valueTiers = String(this.dataset.valueTiers || '').split(',').map(function (p) { var q = p.split(':'); return { spend: parseFloat(q[0]) * 100, pct: parseFloat(q[1]) }; }).filter(function (t) { return t.spend > 0 && t.pct > 0; }).sort(function (x, y) { return x.spend - y.spend; });
      this.cartSubtotal = parseInt(this.dataset.cartSubtotal, 10) || 0;
      this.addonEl = this.querySelector('[data-lp-addon]');
      this.socks = this.addonEl ? parse(this.addonEl.querySelector('[data-lp-addon-data]')) : [];
      this.addon = { on: false, style: 0, colour: null, size: null, pack: 0 };
      if (this.socks.length) { var fv = this.socks[0].variants.filter(function (v) { return v.available; })[0] || this.socks[0].variants[0]; if (fv) { this.addon.colour = fv.colour; this.addon.size = fv.size; } }
      if (this.addonEl) this.watchCart();
      this.pieces.forEach(function (p) {
        p.ci = -1;
        p.options.forEach(function (o, i) { if (isColour(o.name)) p.ci = i; });
        p.tiers = parseTiers(p.offer);
      });
      this.looks = parse(this.querySelector('[data-lp-looks]'));
      this.stage = this.dataset.stage === 'cutouts' ? 'cutouts' : 'photo';
      this.swatches = slice.call(this.querySelectorAll('[data-lp-colour]'));
      this.pieceEls = slice.call(this.querySelectorAll('[data-lp-piece]'));
      this.frame = this.querySelector('.lp-set-switcher__frame');
      /* gallery: the piece's photos by colour (data-lp-gallery), shown after the stage in a snap row */
      this.reel = this.querySelector('[data-lp-reel]');
      this.gallery = this.reel ? (parse(this.querySelector('[data-lp-gallery]')) || {}) : {};
      if (Array.isArray(this.gallery)) this.gallery = {};
      this.cutWrap = this.querySelector('.lp-set-switcher__cutouts');
      this.tintEl = this.querySelector('.lp-set-switcher__tint');
      this.swatchBox = this.querySelector('.lp-set-switcher__swatches');
      this.ring = this.querySelector('.lp-set-switcher__ring');
      this.pieceBox = this.querySelector('.lp-set-switcher__pieces');
      this.thumb = this.querySelector('.lp-set-switcher__thumb');
      this.spots = slice.call(this.querySelectorAll('.lp-set-switcher__spot'));
      this.floor = this.querySelector('.lp-set-switcher__floor');
      this.garments = {};
      slice.call(this.querySelectorAll('[data-lp-garment]')).forEach(function (el) {
        var cuts = el.querySelectorAll('.lp-set-switcher__cut');
        self.garments[el.dataset.lpGarment] = { el: el, lift: el.querySelector('.lp-set-switcher__lift'), front: cuts[0], back: cuts[1], src: cuts[0] ? cuts[0].getAttribute('src') : '' };
      });
      var layers = this.querySelectorAll('.lp-set-switcher__img');
      this.front = layers[0] || null; this.back = layers[1] || null;
      this.imgToken = 0; this.waveToken = 0;
      this.cache = {};
      this.g = null;
      var def = parseInt(this.dataset.default, 10) || 0;
      this.defaultIndex = this.pieces[def] ? def : 0;
      this.index = this.defaultIndex;
      var start = this.pieces[this.index];
      this.colour = this.colourValue(start, this.dataset.colour) || this.firstColour(start);
      this.selected = {};
      this.qty = 1;
      this.shownSrc = this.front && this.front.classList.contains('is-active') ? this.imageFor(start, this.colour) : '';
      this.shown = { colour: this.colour, mode: this.frame ? this.frame.dataset.mode : 'photo', shows: this.frame ? this.frame.dataset.shows : 'both' };
      this.pruneSelected();

      this.onClick = this.handleClick.bind(this);
      this.addEventListener('click', this.onClick);
      /* warm the next colour's cutouts on hover, focus or the start of a tap */
      this.onIntent = function (e) { var t = e.target.closest && e.target.closest('[data-lp-colour]'); if (t && self.contains(t)) self.preload(t.dataset.lpColour); };
      this.addEventListener('pointerover', this.onIntent);
      this.addEventListener('focusin', this.onIntent);
      this.addEventListener('pointerdown', this.onIntent, { passive: true });
      var qi = this.querySelector('[data-lp-qty-input]');
      if (qi) qi.addEventListener('change', function () { self.setQty(parseInt(qi.value, 10)); });
      if (window.LP) {
        this.offColour = window.LP.on('colour-select', function (e) {
          var d = e.detail || {};
          if (!d.colour || d.source === 'lp-set-switcher') return;
          self.setColour(d.colour, false, { external: true });
        });
        this.offProduct = window.LP.on('product-change', function (e) {
          var d = e.detail || {}, i = self.pieceIndexOf(d.handle);
          if (i > -1) self.setPiece(i);
        });
      }
      /* theme editor: selecting a Piece block shows it; selecting a Colour look block shows that colour */
      this.onBlockSelect = function (e) {
        var id = e.detail && e.detail.blockId; if (!id) return;
        for (var i = 0; i < self.pieces.length; i++) { if (self.pieces[i].block === id) { self.setPiece(i); return; } }
        for (var j = 0; j < self.looks.length; j++) { if (self.looks[j].block === id) { self.setColour(self.looks[j].colour, false); return; } }
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.bindSwipe();
      this.render();
      this.placeMarks(false);
      /* deep link: #product-shirt or #<handle> opens that piece; a CTA targeting a piece id does the same */
      this.onHash = function () { self.openHash(location.hash); };
      this.onTargetClick = function (e) { var a = e.target.closest('[data-lp-target]'); if (a) self.openHash(a.getAttribute('data-lp-target'), true); };
      window.addEventListener('hashchange', this.onHash);
      document.addEventListener('click', this.onTargetClick);
      this.openHash(location.hash, false, true);
      if (window.ResizeObserver) {
        this.ro = new ResizeObserver(function () { self.placeMarks(false); });
        if (this.swatchBox) this.ro.observe(this.swatchBox);
        if (this.pieceBox) this.ro.observe(this.pieceBox);
      }
      this.watchView();
      this.watchScreen();
      /* GSAP: fetched once the section is within a screen and a half; the first view plays the entrance only if it
         arrived before the section faded in */
      if (window.LP && window.LP.near && window.LP.gsap) {
        this.offNear = window.LP.near(this, function () {
          self.preload(self.colour);
          window.LP.gsap([self.dataset.lpGsap, self.dataset.lpFlip]).then(function (g) {
            if (!g || !self.isConnected) return;
            self.g = g;
            if (!self.classList.contains('is-visible')) self.entranceArmed = true;
          });
        });
      }
      if (window.MutationObserver) {
        this.mo = new MutationObserver(function () {
          if (!self.classList.contains('is-visible')) return;
          self.mo.disconnect(); self.mo = null;
          if (self.entranceArmed && self.g) self.entrance();
        });
        this.mo.observe(this, { attributes: true, attributeFilter: ['class'] });
      }
    }
    disconnectedCallback() {
      window.removeEventListener('hashchange', this.onHash);
      document.removeEventListener('click', this.onTargetClick);
      if (this.reel && this.onReelScroll) this.reel.removeEventListener('scroll', this.onReelScroll);
      if (this.refreshCart) { document.documentElement.removeEventListener('cart:change', this.refreshCart); document.removeEventListener('cart:refresh', this.refreshCart); }
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerover', this.onIntent);
      this.removeEventListener('focusin', this.onIntent);
      this.removeEventListener('pointerdown', this.onIntent);
      if (this.offColour) this.offColour();
      if (this.offProduct) this.offProduct();
      if (this.offNear) this.offNear();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      if (this.io) this.io.disconnect();
      if (this.screenIO) this.screenIO.disconnect();
      if (this.mo) this.mo.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this.unbindSwipe) this.unbindSwipe();
      clearTimeout(this.flagTimer);
      [this.waveTl, this.flipTl, this.enterTl].forEach(function (t) { if (t) t.kill(); });
    }

    handleClick(e) {
      var t;
      if ((t = e.target.closest('[data-lp-colour]'))) { e.preventDefault(); this.setColour(t.dataset.lpColour, true, { origin: t }); return; }
      if ((t = e.target.closest('[data-lp-piece]'))) { e.preventDefault(); this.setPiece(parseInt(t.dataset.lpPiece, 10)); return; }
      if ((t = e.target.closest('[data-lp-option]'))) {
        e.preventDefault(); if (t.disabled) return;
        this.selected[t.dataset.lpOption] = t.dataset.lpValue;
        this.render({ focus: [t.dataset.lpOption, t.dataset.lpValue] });
        return;
      }
      if ((t = e.target.closest('[data-lp-qty-dec]'))) { e.preventDefault(); this.setQty(this.qty - 1); return; }
      if ((t = e.target.closest('[data-lp-qty-inc]'))) { e.preventDefault(); this.setQty(this.qty + 1); return; }
      if ((t = e.target.closest('[data-lp-guide]'))) { e.preventDefault(); this.openGuide(t.dataset.lpGuide || 'piece'); return; }
      if ((t = e.target.closest('[data-lp-slide-to]'))) { e.preventDefault(); var to = t.dataset.lpSlideTo, n = this.reel.children.length; this.slideTo(to === 'next' ? (this.slideIndex() + 1) % n : to === 'prev' ? (this.slideIndex() - 1 + n) % n : parseInt(to, 10), true); return; }
      if ((t = e.target.closest('[data-lp-addon-toggle]'))) { e.preventDefault(); if (t.disabled) return; this.addon.on = !this.addon.on; this.render(); return; }
      if ((t = e.target.closest('[data-lp-addon-style]'))) { e.preventDefault(); this.addon.style = parseInt(t.dataset.lpAddonStyle, 10); this.settleSock(); this.render(); return; }
      if ((t = e.target.closest('[data-lp-addon-colour]'))) { e.preventDefault(); if (t.disabled) return; this.addon.colour = t.dataset.lpAddonColour; this.settleSock(); this.render(); return; }
      if ((t = e.target.closest('[data-lp-addon-size]'))) { e.preventDefault(); if (t.disabled) return; this.addon.size = t.dataset.lpAddonSize; this.render(); return; }
      if ((t = e.target.closest('[data-lp-addon-pack]'))) { e.preventDefault(); this.addon.pack = parseInt(t.dataset.lpAddonPack, 10); this.render(); return; }
      if ((t = e.target.closest('[data-lp-atc]'))) { e.preventDefault(); this.add(); }
    }

    /* ---- data ---- */
    colourValues(p) { return p && p.ci >= 0 ? p.options[p.ci].values : []; }
    hasColour(p, c) { if (!p) return false; if (p.ci < 0) return true; var k = norm(c); return this.colourValues(p).some(function (v) { return norm(v) === k; }); }
    colourValue(p, c) {
      if (!p || !c) return null;
      if (p.ci < 0) return c;
      var k = norm(c), vals = this.colourValues(p);
      for (var i = 0; i < vals.length; i++) { if (norm(vals[i]) === k) return vals[i]; }
      return null;
    }
    variantsFor(p, c) { if (p.ci < 0) return p.variants; var k = norm(c), ci = p.ci; return p.variants.filter(function (v) { return norm(v.options[ci]) === k; }); }
    inStock(p, c) { return this.variantsFor(p, c).some(function (v) { return v.available; }); }
    firstColour(p) {
      var self = this;
      var own = this.swatches.map(function (b) { return b.dataset.lpColour; }).filter(function (c) { return self.hasColour(p, c); });
      if (!own.length) own = this.colourValues(p).slice();
      var stocked = own.filter(function (c) { return self.inStock(p, c); });
      return this.colourValue(p, stocked[0] || own[0]) || this.colour || '';
    }
    pieceWith(c) {
      if (this.hasColour(this.pieces[this.defaultIndex], c)) return this.defaultIndex;
      for (var i = 0; i < this.pieces.length; i++) { if (this.hasColour(this.pieces[i], c)) return i; }
      return -1;
    }
    pieceIndexOf(handle) { for (var i = 0; i < this.pieces.length; i++) { if (handle && this.pieces[i].handle === handle) return i; } return -1; }
    imageFor(p, c) { var vs = this.variantsFor(p, c); for (var i = 0; i < vs.length; i++) { if (vs[i].image) return vs[i].image; } return p.image || ''; }
    minPrice(p, c) { var vs = this.variantsFor(p, c); if (!vs.length) vs = p.variants; return Math.min.apply(null, vs.map(function (v) { return v.price; })); }
    optionIndexes(p) { return p.options.map(function (o, i) { return i; }).filter(function (i) { return i !== p.ci; }); }
    variant() {
      var p = this.pieces[this.index], self = this, idx = this.optionIndexes(p);
      for (var k = 0; k < idx.length; k++) { if (this.selected[p.options[idx[k]].name] == null) return null; }
      return this.variantsFor(p, this.colour).filter(function (v) {
        return idx.every(function (i) { return v.options[i] === self.selected[p.options[i].name]; });
      })[0] || null;
    }
    /* keep picks the new piece/colour can honour (a size carries across pieces); drop sold-out sizes; pre-pick
       every non-size option with its first available value */
    pruneSelected() {
      var p = this.pieces[this.index], self = this, vs = this.variantsFor(p, this.colour), next = {};
      this.optionIndexes(p).forEach(function (i) {
        var o = p.options[i], cur = self.selected[o.name];
        var ok = function (val) { return vs.some(function (v) { return v.available && v.options[i] === val; }); };
        if (cur != null && o.values.indexOf(cur) > -1 && ok(cur)) { next[o.name] = cur; return; }
        if (isSize(o.name)) return;
        var pick = o.values.filter(ok)[0];
        next[o.name] = pick != null ? pick : o.values[0];
      });
      this.selected = next;
    }
    lookFor(c) { var k = norm(c); for (var i = 0; i < this.looks.length; i++) { if (norm(this.looks[i].colour) === k) return this.looks[i]; } return null; }
    tintFor(c) { var k = norm(c); for (var i = 0; i < this.swatches.length; i++) { if (norm(this.swatches[i].dataset.lpColour) === k) return this.swatches[i].dataset.lpTint || ''; } return ''; }
    /* what the stage should show for a piece in a colour */
    target(p, c) {
      var shows = p.shows || 'both', look = this.stage === 'cutouts' ? this.lookFor(c) : null;
      var has = function (slot) { return !!(look && look[slot] && look[slot].src); };
      var ok = !!look && (shows === 'bottom' || has('top')) && (shows === 'top' || has('bottom'));
      return { colour: c, mode: ok ? 'cutouts' : 'photo', shows: shows, look: look };
    }

    /* ---- actions ---- */
    setColour(c, announce, opts) {
      if (!this.hasColour(this.pieces[this.index], c)) { var j = this.pieceWith(c); if (j < 0) return; this.index = j; }
      this.colour = this.colourValue(this.pieces[this.index], c) || c;
      this.pruneSelected();
      this.render(opts || {});
      if (announce) this.announce();
    }
    pieceIndexOfId(id) {
      var k = String(id || '').replace(/^#/, '').toLowerCase(); if (!k) return -1;
      try { k = decodeURIComponent(k); } catch (e) {}
      for (var n = 0; n < this.pieceEls.length; n++) { if ((this.pieceEls[n].id || '').toLowerCase() === k) return parseInt(this.pieceEls[n].dataset.lpPiece, 10); }
      return this.pieceIndexOf(k);
    }
    openHash(hash, noScroll, initial) {
      var i = this.pieceIndexOfId(hash); if (i < 0) return;
      if (i !== this.index) this.setPiece(i);
      if (noScroll) return;
      if (initial) { this.land(); return; }
      if (window.LP && window.LP.scrollTo) window.LP.scrollTo(this); else this.scrollIntoView();
    }
    /* deep link on page load: the browser has already jumped to the piece button and the hero above is still
       settling (video poster, fonts, lazy media), so a smooth scroll aimed now lands short or long. Jump straight
       to the section top instead and keep re-aiming for ~1.5s and once more on load, until the top stops moving. */
    land() {
      var self = this, last = null, n = 0;
      var go = function () {
        var top = Math.round(self.getBoundingClientRect().top + window.pageYOffset);
        if (top !== last) { last = top; window.scrollTo({ top: top, behavior: 'instant' }); }
        if (++n < 8) setTimeout(go, 200);
      };
      go();
      /* the browser re-applies its own jump to the hash at load; land again just after it */
      if (document.readyState !== 'complete') window.addEventListener('load', function () { last = null; n = 0; setTimeout(go, 50); }, { once: true });
    }
    setPiece(i) {
      var p = this.pieces[i]; if (!p) return;
      var moved = false, from = this.index;
      this.index = i;
      if (!this.hasColour(p, this.colour)) { this.colour = this.firstColour(p); moved = true; }
      else this.colour = this.colourValue(p, this.colour) || this.colour;
      this.pruneSelected();
      var btn = this.pieceEls.filter(function (b) { return parseInt(b.dataset.lpPiece, 10) === i; })[0];
      this.render({ piece: i !== from, dir: i < from ? -1 : 1, origin: btn });
      if (moved) this.announce();
    }
    /* touch swipe on the stage: the next or previous colour, sweeping in from that side */
    step(dir) {
      var list = this.swatches.map(function (b) { return b.dataset.lpColour; });
      if (list.length < 2) return;
      var k = norm(this.colour), i = 0;
      for (var n = 0; n < list.length; n++) { if (norm(list[n]) === k) { i = n; break; } }
      var r = this.frame.getBoundingClientRect();
      this.setColour(list[(i + dir + list.length) % list.length], true, { origin: { x: dir > 0 ? r.right : r.left, y: r.top + r.height * 0.45 } });
    }
    announce() { if (window.LP) window.LP.emit('colour-select', { colour: this.colour, handle: this.pieces[this.index].handle, source: 'lp-set-switcher' }); }
    /* what is picked right now (the sticky bar follows it): piece, colour, its photo and from-price */
    selection() {
      var p = this.pieces[this.index], c = this.colour;
      return { handle: p.handle, title: p.title, label: p.label, url: p.url, colour: c, image: this.imageFor(p, c), price: this.minPrice(p, c), source: 'lp-set-switcher' };
    }
    publish() {
      var sel = this.selection(), key = sel.handle + '|' + norm(sel.colour);
      if (key === this.published) return;
      this.published = key;
      if (window.LP) window.LP.emit('selection', sel);
    }
    setQty(n) { this.qty = isNaN(n) ? 1 : Math.max(1, Math.min(99, n)); this.render(); }

    /* ---- view ---- */
    render(opts) {
      opts = opts || {};
      var self = this, p = this.pieces[this.index], c = this.colour, v = this.variant(), stock = this.inStock(p, c);
      this.publish();
      var soldOut = this.dataset.soldOut || 'Sold out', unavailable = this.dataset.unavailable || 'Not in {colour}';

      /* each selector shows the other's gaps, without text: a colour the chosen piece doesn't come in (or is sold out
         in) gets a slash; a piece that doesn't come in the chosen colour (or is sold out in it) is struck through.
         Both stay tappable (setColour / setPiece move to a combination that exists). Screen readers hear why. */
      this.swatches.forEach(function (b) {
        var sc = b.dataset.lpColour, made = self.hasColour(p, sc), ok = made && self.inStock(p, sc);
        b.setAttribute('aria-pressed', norm(sc) === norm(c) ? 'true' : 'false');
        b.classList.toggle('is-unavailable', !ok);
        /* a colour the chosen piece is not made in is hidden outright; a sold-out one stays, slashed */
        b.hidden = !made;
        b.setAttribute('aria-label', sc + (!made ? '. ' + p.label + ': ' + unavailable.replace('{colour}', sc) : (!ok ? '. ' + soldOut : '')));
      });
      this.roll(this.querySelector('[data-lp-colour-name]'), c, 1);

      this.pieceEls.forEach(function (b) {
        var i = parseInt(b.dataset.lpPiece, 10), q = self.pieces[i]; if (!q) return;
        var made = self.hasColour(q, c), ok = made && self.inStock(q, c);
        b.setAttribute('aria-pressed', i === self.index ? 'true' : 'false');
        b.classList.toggle('is-unavailable', !ok);
        b.classList.toggle('is-sold-out', made && !ok);
        var status = b.querySelector('[data-lp-piece-status]');
        if (status) status.textContent = !made ? unavailable.replace('{colour}', c) : (!ok ? soldOut : '');
      });
      this.placeMarks(true);

      var badge = this.querySelector('[data-lp-badge]'); if (badge) badge.hidden = stock;
      var shown = v || this.variantsFor(p, c)[0] || p.variants[0];
      var dir = opts.dir || 1;
      this.roll(this.querySelector('[data-lp-title]'), p.title, dir);
      /* the order-value nudge (if any) first: the piece's price shows the tier the order lands in with the socks */
      this.addonExtra = 0;
      this.renderAddon(p, shown, stock);
      var orderPct = shown && this.valueTiers.length ? this.tierPct(this.cartSubtotal + this.qty * shown.price + this.addonExtra) : 0;
      var shownPrice = shown ? Math.round(shown.price * (1 - orderPct / 100)) : 0;
      var pr = this.querySelector('[data-lp-price]');
      if (pr && shown) {
        var was = parseFloat(pr.dataset.cents || '0');
        pr.dataset.cents = shownPrice;
        this.roll(pr, money(shownPrice, this.format), shownPrice < was ? -1 : 1);
      }
      var cp = this.querySelector('[data-lp-compare]');
      if (cp) { var cmp = shown ? Math.max(shown.compare_at_price > shown.price ? shown.compare_at_price : 0, orderPct > 0 ? shown.price : 0) : 0; cp.hidden = !cmp; cp.textContent = cmp ? money(cmp, this.format) : ''; }

      var of = this.querySelector('[data-lp-offer]');
      if (of) { var txt = p.tiers.map(function (x) { return 'Buy ' + x.count + ', save ' + x.pct + '%'; }).join(' · '); if (txt) of.textContent = txt; this.fold(of, !!txt && stock); }
      var nd = this.querySelector('[data-lp-nudge]');
      if (nd) { var hit = p.tiers.filter(function (x) { return self.qty >= x.count; }).pop(); nd.hidden = !hit || !stock; nd.textContent = hit ? hit.pct + '% off applied at checkout' : ''; }

      this.renderGallery(p, c);
      this.renderOptions(p, c, opts.focus, opts.piece);

      var qi = this.querySelector('[data-lp-qty-input]'); if (qi) qi.value = this.qty;
      var qb = this.querySelector('[data-lp-qty]'); if (qb) qb.hidden = !stock;
      var btn = this.querySelector('[data-lp-atc]'), lbl = this.querySelector('[data-lp-atc-label]');
      if (btn) btn.disabled = !stock || !!(v && !v.available);
      if (lbl) lbl.textContent = !stock || (v && !v.available) ? soldOut : (v ? (this.dataset.atc || 'Add to Cart') : (this.dataset.sizePrompt || 'Select a size'));
      var err = this.querySelector('[data-lp-error]'); if (err) err.hidden = true;

      this.paint(opts);
    }
    renderOptions(p, c, focus, fresh) {
      var box = this.querySelector('[data-lp-options]'); if (!box) return;
      var self = this, vs = this.variantsFor(p, c), idx = this.optionIndexes(p), html = '';
      var tpl = this.querySelector('[data-lp-guide-tpl]');
      idx.forEach(function (i) {
        var o = p.options[i], size = isSize(o.name), label = size ? (self.dataset.sizeLabel || 'Size') : o.name, cur = self.selected[o.name];
        var pills = o.values.map(function (val) {
          var avail = vs.some(function (v) {
            return v.available && v.options[i] === val && idx.every(function (j) { var sel = self.selected[p.options[j].name]; return j === i || sel == null || v.options[j] === sel; });
          });
          return '<button type="button" class="lp-set-switcher__pill" data-lp-option="' + esc(o.name) + '" data-lp-value="' + esc(val) + '" aria-pressed="' + (cur === val ? 'true' : 'false') + '"' + (avail ? '' : ' disabled') + '>' + esc(size ? shortSize(val) : val) + '</button>';
        }).join('');
        html += '<fieldset class="lp-set-switcher__group" data-lp-group="' + esc(o.name) + '">'
          + '<legend class="lp-sr-only">' + esc(label) + '</legend>'
          + '<div class="lp-set-switcher__group-head"><span class="lp-set-switcher__label" aria-hidden="true">' + esc(label) + '<span class="lp-set-switcher__label-value">' + esc(cur || '') + '</span></span>'
          + (size && tpl ? tpl.innerHTML : '') + '</div>'
          + '<div class="lp-set-switcher__pills">' + pills + '</div></fieldset>';
      });
      box.innerHTML = html;
      if (focus) {
        var el = box.querySelector('[data-lp-option="' + cssEsc(focus[0]) + '"][data-lp-value="' + cssEsc(focus[1]) + '"]');
        if (el) el.focus({ preventScroll: true });
      }
      /* a new piece: its pills fade up in a quick wave */
      var g = this.g;
      if (fresh && g && this.inView) {
        var pills = box.querySelectorAll('.lp-set-switcher__pill');
        g.set(pills, { transition: 'none' });
        g.from(pills, { opacity: 0, y: 8, duration: 0.35, stagger: 0.03, ease: 'power2.out', onComplete: function () { g.set(pills, { clearProps: 'transition,opacity,transform' }); } });
      }
    }
    /* the ring over the picked swatch and the thumb under the picked piece; `animate` false jumps without a glide */
    placeMarks(animate) {
      var pressed = function (els) { return els.filter(function (b) { return b.getAttribute('aria-pressed') === 'true'; })[0]; };
      /* `key` names the custom properties: r → --lp-set-switcher-rx / -ry (ring), t → -tx / -ty / -tw / -th (thumb) */
      var place = function (box, mark, on, flag, key) {
        if (!box || !mark) return;
        if (!on) { box.classList.remove(flag); return; }
        if (!animate) box.classList.remove('is-ready');
        mark.style.setProperty('--lp-set-switcher-' + key + 'x', on.offsetLeft + 'px');
        mark.style.setProperty('--lp-set-switcher-' + key + 'y', on.offsetTop + 'px');
        if (key === 't') { mark.style.setProperty('--lp-set-switcher-tw', on.offsetWidth + 'px'); mark.style.setProperty('--lp-set-switcher-th', on.offsetHeight + 'px'); }
        box.classList.add(flag);
        /* the glide is switched on two frames after a jump, so the jump itself never animates */
        if (!box.classList.contains('is-ready')) {
          window.requestAnimationFrame(function () { window.requestAnimationFrame(function () { box.classList.add('is-ready'); }); });
        }
      };
      place(this.swatchBox, this.ring, pressed(this.swatches), 'has-ring', 'r');
      place(this.pieceBox, this.thumb, pressed(this.pieceEls), 'has-thumb', 't');
    }
    /* text that rolls to its new value; `dir` 1 rolls up, -1 down */
    roll(el, text, dir) {
      if (!el) return;
      text = String(text == null ? '' : text);
      if (el.dataset.text == null) el.dataset.text = el.textContent.trim();
      if (el.dataset.text === text) return;
      el.dataset.text = text;
      if (el._roll) el._roll.progress(1);
      var g = this.g;
      if (!g || !this.inView) { el.textContent = text; return; }
      var from = document.createElement('span'), to = document.createElement('span');
      from.textContent = el.textContent; to.textContent = text;
      el.textContent = ''; el.appendChild(from); el.appendChild(to);
      el._roll = g.timeline({ onComplete: function () { el.textContent = text; el._roll = null; } })
        .to(from, { yPercent: -100 * dir, opacity: 0, duration: 0.35, ease: 'power2.in' }, 0)
        .fromTo(to, { yPercent: 100 * dir, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.5, ease: 'power3.out' }, 0.12);
    }
    /* the offer line folds open or shut */
    fold(el, show) {
      var g = this.g;
      if (el._fold) { el._fold.kill(); el._fold = null; if (g) g.set(el, { clearProps: 'height,opacity' }); }
      if (!g || !this.inView) { el.hidden = !show; return; }
      if (show === !el.hidden) return;
      if (show) {
        el.hidden = false;
        el._fold = g.fromTo(el, { height: 0, opacity: 0 }, { height: 'auto', opacity: 1, duration: 0.45, ease: 'power2.out', clearProps: 'height,opacity', onComplete: function () { el._fold = null; } });
      } else {
        el._fold = g.to(el, { height: 0, opacity: 0, duration: 0.3, ease: 'power2.in', onComplete: function () { el.hidden = true; g.set(el, { clearProps: 'height,opacity' }); el._fold = null; } });
      }
    }

    /* ---- stage ---- */
    paint(opts) {
      var p = this.pieces[this.index], c = this.colour, next = this.target(p, c), prev = this.shown;
      if (this.frame) this.frame.setAttribute('aria-label', p.title + ' · ' + c);
      var colourChanged = norm(prev.colour) !== norm(c);
      var layoutChanged = prev.mode !== next.mode || prev.shows !== next.shows;
      var live = !!this.g && motionOK();
      /* a colour picked elsewhere while this section is off screen sweeps in when it arrives */
      if (colourChanged && opts.external && live && !this.inView) { this.deferred = true; return; }
      this.deferred = false;
      if (next.mode === 'photo') this.setImage(this.imageFor(p, c));
      if (!colourChanged && !layoutChanged) return;
      this.shown = { colour: c, mode: next.mode, shows: next.shows };
      if (colourChanged) {
        if (live) this.sweep(next, this.originFor(opts.origin, c), !layoutChanged);
        else this.recolour(next);
      }
      if (layoutChanged) this.flip(next, live);
    }
    /* where the sweep starts, in viewport px: a point, an element's centre, or the colour's swatch */
    originFor(o, c) {
      if (o && typeof o.x === 'number') return o;
      var el = o && o.getBoundingClientRect ? o : null;
      if (!el) { var k = norm(c); el = this.swatches.filter(function (b) { return norm(b.dataset.lpColour) === k; })[0] || this.frame || this; }
      var r = el.getBoundingClientRect();
      return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    }
    /* no motion: the new colour everywhere at once */
    recolour(next) {
      this.settle();
      var self = this, tint = this.tintFor(next.colour);
      this.setTint(tint);
      var spot = this.spots.filter(function (s) { return s.classList.contains('is-active'); })[0];
      if (spot && this.tintEl) this.setSpotTint(spot, tint);
      if (!next.look) return;
      ['top', 'bottom'].forEach(function (slot) {
        var gm = self.garments[slot], cut = next.look[slot];
        if (!gm || !cut || !cut.src || gm.src === cut.src) return;
        self.load(gm.front, cut); gm.src = cut.src;
      });
    }
    setTint(tint) {
      if (!this.tintEl) return;
      if (tint) this.style.setProperty('--lp-set-switcher-tint', tint); else this.style.removeProperty('--lp-set-switcher-tint');
      this.style.removeProperty('--lp-set-switcher-tint-next');
      this.tintEl.style.setProperty('--lp-set-switcher-wr', '0px');
    }
    setSpotTint(spot, tint) { if (tint) spot.style.setProperty('--lp-set-switcher-spot-tint', tint); else spot.style.removeProperty('--lp-set-switcher-spot-tint'); }
    load(img, cut) {
      if (cut.srcset) { img.sizes = this.dataset.cutSizes || ''; img.srcset = cut.srcset; } else { img.removeAttribute('srcset'); img.removeAttribute('sizes'); }
      img.src = cut.src;
      img.removeAttribute('loading');
    }
    /* fetch and decode a colour's cutouts ahead of the tap; Files URLs also tell us the garment's shape */
    preload(c) {
      var look = this.lookFor(c), self = this;
      if (!look || this.stage !== 'cutouts') return Promise.resolve();
      return Promise.all(['top', 'bottom'].map(function (slot) {
        var cut = look[slot]; if (!cut || !cut.src) return null;
        if (!self.cache[cut.src]) {
          var img = new Image();
          img.decoding = 'async';
          if (cut.srcset) { img.sizes = self.dataset.cutSizes || ''; img.srcset = cut.srcset; }
          img.src = cut.src;
          self.cache[cut.src] = decoded(img).then(function () {
            if (!cut.ar && img.naturalWidth && self.frame) {
              cut.ar = img.naturalWidth / img.naturalHeight;
              var prop = '--lp-set-switcher-ar-' + slot;
              if (!self.frame.style.getPropertyValue(prop)) self.frame.style.setProperty(prop, cut.ar.toFixed(4));
            }
          });
        }
        return self.cache[cut.src];
      }));
    }
    /* the colour sweep: the section tint grows out from `origin` in a soft-edged circle; with `masked` the garments
       change colour as the edge passes them (else they crossfade, e.g. while they are also moving); the lit spot
       crossfades as the edge reaches the stage centre */
    sweep(next, origin, masked) {
      this.settle();
      var self = this, token = ++this.waveToken, look = next.look, slots = [];
      if (this.stage === 'cutouts' && look) {
        ['top', 'bottom'].forEach(function (slot) {
          var gm = self.garments[slot], cut = look[slot];
          if (!gm || !cut || !cut.src || gm.src === cut.src) return;
          clearMask(gm.back);
          gm.back.classList.remove('is-active');
          gm.back.parentNode.appendChild(gm.back); /* the incoming layer paints over the outgoing one */
          self.load(gm.back, cut);
          slots.push({ gm: gm, cut: cut });
        });
      }
      var ready = Promise.all(slots.map(function (s) { return decoded(s.gm.back); }));
      Promise.race([ready, new Promise(function (r) { setTimeout(r, 350); })]).then(function () {
        if (token !== self.waveToken || !self.isConnected || !self.g) return;
        self.runSweep(next, origin, slots, masked);
      });
    }
    runSweep(next, origin, slots, masked) {
      var g = this.g, self = this, box = this.getBoundingClientRect();
      var ox = origin.x - box.left, oy = origin.y - box.top;
      var R = Math.max(Math.hypot(ox, oy), Math.hypot(box.width - ox, oy), Math.hypot(ox, box.height - oy), Math.hypot(box.width - ox, box.height - oy)) + FEATHER;
      var dur = Math.min(1.2, Math.max(0.8, R / 1500));
      var tint = this.tintFor(next.colour);
      if (this.tintEl) {
        if (tint) this.style.setProperty('--lp-set-switcher-tint-next', tint); else this.style.setProperty('--lp-set-switcher-tint-next', 'var(--s-bg)');
        this.tintEl.style.setProperty('--lp-set-switcher-wx', ox + 'px');
        this.tintEl.style.setProperty('--lp-set-switcher-wy', oy + 'px');
        this.tintEl.style.setProperty('--lp-set-switcher-wr', '0px');
      }
      var layers = slots.map(function (s) {
        var r = s.gm.back.getBoundingClientRect(), l = { gm: s.gm, cut: s.cut, x: origin.x - r.left, y: origin.y - r.top };
        if (masked) setMask(s.gm.back, l.x, l.y, 0);
        s.gm.back.classList.add('is-active');
        return l;
      });
      var fr = this.frame ? this.frame.getBoundingClientRect() : box;
      var meet = inOut2At(Math.hypot(fr.left + fr.width / 2 - origin.x, fr.top + fr.height / 2 - origin.y) / R) * dur;
      var proxy = { r: 0 };
      var tl = g.timeline({ onComplete: function () { self.endSweep(); } });
      tl.to(proxy, {
        r: R, duration: dur, ease: 'power2.inOut',
        onUpdate: function () {
          if (self.tintEl) self.tintEl.style.setProperty('--lp-set-switcher-wr', proxy.r + 'px');
          if (masked) layers.forEach(function (l) { setMask(l.gm.back, l.x, l.y, proxy.r); });
        }
      }, 0);
      if (!masked && layers.length) tl.fromTo(layers.map(function (l) { return l.gm.back; }), { opacity: 0 }, { opacity: 1, duration: 0.5, ease: 'power1.inOut' }, 0);
      var spotOut = this.spots.filter(function (s) { return s.classList.contains('is-active'); })[0];
      var spotIn = this.spots.filter(function (s) { return s !== spotOut; })[0];
      if (this.tintEl && spotOut && spotIn) {
        this.setSpotTint(spotIn, tint);
        tl.fromTo(spotIn, { opacity: 0 }, { opacity: 1, duration: 0.7, ease: 'power1.inOut' }, Math.max(0, meet - 0.25));
        tl.to(spotOut, { opacity: 0, duration: 0.7, ease: 'power1.inOut' }, '<');
      }
      this.waveTl = tl;
      this.wave = { tint: tint, layers: layers, spotIn: spotIn, spotOut: spotOut };
    }
    endSweep() {
      var w = this.wave, g = this.g; if (!w) return;
      this.wave = null; this.waveTl = null;
      this.setTint(w.tint);
      w.layers.forEach(function (l) {
        var gm = l.gm;
        clearMask(gm.back);
        gm.front.classList.remove('is-active');
        if (g) g.set([gm.front, gm.back], { clearProps: 'opacity' });
        var t = gm.front; gm.front = gm.back; gm.back = t;
        gm.src = l.cut.src;
      });
      if (w.spotIn && w.spotOut) {
        w.spotIn.classList.add('is-active'); w.spotOut.classList.remove('is-active');
        if (g) g.set([w.spotIn, w.spotOut], { clearProps: 'opacity' });
      }
    }
    /* finish a sweep in flight at once, so the next one starts from a settled colour */
    settle() {
      this.waveToken++;
      if (this.waveTl) this.waveTl.progress(1);
      if (this.wave) this.endSweep();
    }
    /* move between Set / top / bottom: Flip from the old places to the new. The garment that leaves shrinks towards
       its edge and fades out early; the one that arrives fades in a beat later. The fades run on each garment's lift,
       not on the garment: Flip restores the garment's inline style when it finishes, which would undo a fade. Opacity
       starts from wherever it is, so a tap mid-move carries on smoothly. */
    flip(next, live) {
      var frame = this.frame; if (!frame) return;
      var F = window.Flip, g = this.g, self = this;
      var gms = ['top', 'bottom'].map(function (s) { return self.garments[s]; }).filter(Boolean);
      var lifts = gms.map(function (gm) { return gm.lift; }).filter(Boolean);
      var targets = gms.map(function (gm) { return gm.el; }).concat(this.floor ? [this.floor] : []);
      if (this.enterTl) this.enterTl.progress(1);
      if (!live || !F || !targets.length || frame.dataset.mode !== 'cutouts' || next.mode !== 'cutouts') {
        if (g && lifts.length) { g.killTweensOf(lifts, 'opacity'); g.set(lifts, { clearProps: 'opacity' }); }
        frame.dataset.mode = next.mode; frame.dataset.shows = next.shows;
        return;
      }
      lifts.forEach(function (el) { g.killTweensOf(el, 'opacity'); g.set(el, { opacity: getComputedStyle(el).opacity }); });
      var state = F.getState(targets);
      frame.dataset.shows = next.shows;
      var tl = g.timeline({ onComplete: function () { if (self.flipTl === tl) self.flipTl = null; } });
      tl.add(F.from(state, { duration: 0.85, ease: 'power3.inOut', scale: true }), 0);
      gms.forEach(function (gm) {
        if (!gm.lift) return;
        var on = next.shows === 'both' || gm.el.dataset.lpGarment === next.shows;
        tl.to(gm.lift, on ? { opacity: 1, duration: 0.55, ease: 'power2.out', clearProps: 'opacity' } : { opacity: 0, duration: 0.4, ease: 'power2.in', clearProps: 'opacity' }, on ? 0.22 : 0);
      });
      this.flipTl = tl;
    }
    /* first view: the garments rise in one after the other, the floor shadow spreads, the swatches pop */
    entrance() {
      var g = this.g, self = this; if (!g) return;
      var tl = g.timeline({ defaults: { ease: 'power3.out' }, onComplete: function () { self.enterTl = null; } });
      if (this.frame && this.frame.dataset.mode === 'cutouts') {
        var lifts = ['bottom', 'top'].map(function (s) { return self.garments[s] && self.garments[s].lift; }).filter(Boolean);
        var spot = this.spots.filter(function (s) { return s.classList.contains('is-active'); });
        tl.from(spot, { scale: 0.6, opacity: 0, duration: 1.3, clearProps: 'transform,opacity' }, 0)
          .from(this.floor, { opacity: 0, duration: 1, clearProps: 'opacity' }, 0.3)
          .from(lifts, { yPercent: 14, opacity: 0, scale: 0.92, rotation: function (i) { return i ? -3 : 2; }, duration: 1.1, stagger: 0.16, clearProps: 'transform,opacity' }, 0.1);
      }
      if (this.swatches.length) {
        g.set(this.swatches, { transition: 'none' });
        tl.from(this.swatches, { scale: 0, opacity: 0, duration: 0.6, stagger: 0.06, ease: 'back.out(2)', onComplete: function () { g.set(self.swatches, { clearProps: 'transition,transform,opacity' }); } }, 0.15);
      }
      this.enterTl = tl;
    }
    setImage(src) {
      if (!src || !this.front || !this.back) return;
      if (src === this.shownSrc) return;
      this.shownSrc = src;
      var self = this, token = ++this.imgToken;
      var swap = function () {
        if (token !== self.imgToken) return;
        var back = self.back, front = self.front;
        back.removeAttribute('srcset'); back.removeAttribute('sizes');
        back.src = src;
        back.classList.add('is-active');
        front.classList.remove('is-active');
        self.front = back; self.back = front;
      };
      var pre = new Image();
      pre.onload = swap; pre.onerror = swap;
      pre.src = src;
    }
    bindSwipe() {
      var frame = this.frame, self = this;
      if (!frame || this.reel || this.stage !== 'cutouts' || !window.PointerEvent) return; /* in a gallery the swipe moves photos */
      var d = null;
      var down = function (e) { if (e.pointerType === 'mouse' || !e.isPrimary) return; d = { x: e.clientX, y: e.clientY, dx: 0, on: false }; };
      var move = function (e) {
        if (!d) return;
        d.dx = e.clientX - d.x;
        var dy = e.clientY - d.y;
        if (!d.on && Math.abs(d.dx) > 12 && Math.abs(d.dx) > Math.abs(dy) * 1.4) { d.on = true; self.preloadAround(); }
        if (d.on && self.g && self.cutWrap && frame.dataset.mode === 'cutouts') self.g.set(self.cutWrap, { x: d.dx * 0.16 });
      };
      var up = function (e) {
        var s = d; d = null; if (!s) return;
        if (self.g && self.cutWrap) self.g.to(self.cutWrap, { x: 0, duration: 0.7, ease: 'elastic.out(1, 0.55)', clearProps: 'transform' });
        if (e.type === 'pointerup' && s.on && Math.abs(s.dx) > 44) self.step(s.dx < 0 ? 1 : -1);
      };
      frame.addEventListener('pointerdown', down, { passive: true });
      frame.addEventListener('pointermove', move, { passive: true });
      frame.addEventListener('pointerup', up);
      frame.addEventListener('pointercancel', up);
      this.unbindSwipe = function () {
        frame.removeEventListener('pointerdown', down); frame.removeEventListener('pointermove', move);
        frame.removeEventListener('pointerup', up); frame.removeEventListener('pointercancel', up);
      };
    }
    preloadAround() {
      var list = this.swatches.map(function (b) { return b.dataset.lpColour; }), k = norm(this.colour), self = this;
      list.forEach(function (c, i) { if (norm(c) === k) { self.preload(list[(i + 1) % list.length]); self.preload(list[(i - 1 + list.length) % list.length]); } });
    }
    flagMissing() {
      var p = this.pieces[this.index], self = this;
      var missing = this.optionIndexes(p).map(function (i) { return p.options[i].name; }).filter(function (n) { return self.selected[n] == null; })[0];
      var group = missing != null ? this.querySelector('[data-lp-group="' + cssEsc(missing) + '"]') : null;
      if (!group) return;
      group.classList.remove('is-invalid'); void group.offsetWidth; group.classList.add('is-invalid');
      clearTimeout(this.flagTimer);
      this.flagTimer = setTimeout(function () { group.classList.remove('is-invalid'); }, 900);
      var first = group.querySelector('.lp-set-switcher__pill:not([disabled])'); if (first) first.focus({ preventScroll: true });
    }

    /* ---- gallery: slide one is the stage; the piece's photos in the chosen colour follow ---- */
    photosFor(p, c) {
      var byColour = this.gallery[p.handle] || {}, k = norm(c);
      for (var key in byColour) { if (norm(key) === k) return byColour[key] || []; }
      return byColour.all || [];
    }
    renderGallery(p, c) {
      var reel = this.reel; if (!reel) return;
      var key = p.handle + '|' + norm(c);
      if (key === this.galleryKey) return;
      this.galleryKey = key;
      var photos = this.photosFor(p, c), self = this;
      slice.call(reel.querySelectorAll('.lp-set-switcher__slide:not(.lp-set-switcher__slide--stage)')).forEach(function (el) { el.remove(); });
      photos.forEach(function (src, i) {
        var li = document.createElement('div'); li.className = 'lp-set-switcher__slide';
        li.innerHTML = '<img src="' + esc(src) + '" alt="' + esc(p.title + ' in ' + c + ', photo ' + (i + 1)) + '" loading="lazy" width="1200" height="1200">';
        reel.appendChild(li);
      });
      reel.scrollTo({ left: 0, behavior: 'auto' });
      var dots = this.querySelector('[data-lp-dots]');
      slice.call(this.querySelectorAll('[data-lp-nav]')).forEach(function (b) { b.hidden = photos.length < 1; });
      if (dots) {
        var n = photos.length + 1;
        dots.hidden = n < 2;
        dots.dataset.count = n;
        dots.innerHTML = '<span class="lp-set-switcher__rail" aria-hidden="true"><i data-lp-rail></i></span>'
          + '<span class="lp-sr-only" aria-live="polite" data-lp-pager>Photo 1 of ' + n + '</span>';
        dots.classList.add('is-stage');
        dots.style.setProperty('--lp-set-switcher-progress', (1 / n).toFixed(4));
      }
      if (!this.reelBound) {
        this.reelBound = true;
        var raf = 0;
        this.onReelScroll = function () { cancelAnimationFrame(raf); raf = requestAnimationFrame(function () { self.syncDots(); }); };
        reel.addEventListener('scroll', this.onReelScroll, { passive: true });
      }
    }
    slideTo(i, smooth) {
      var reel = this.reel, slide = reel && reel.children[i]; if (!slide) return;
      reel.scrollTo({ left: slide.offsetLeft, behavior: smooth && motionOK() ? 'smooth' : 'auto' });
    }
    slideIndex() { var reel = this.reel; return reel ? Math.round(reel.scrollLeft / Math.max(1, reel.clientWidth)) : 0; }
    /* the hairline fills with the slide (faint on the stage); the count is read out to screen readers only */
    syncDots() {
      var reel = this.reel, dots = this.querySelector('[data-lp-dots]'); if (!reel || !dots) return;
      var i = this.slideIndex(), n = reel.children.length;
      dots.classList.toggle('is-stage', i === 0);
      dots.style.setProperty('--lp-set-switcher-progress', ((i + 1) / n).toFixed(4));
      var p = dots.querySelector('[data-lp-pager]'); if (p) p.textContent = 'Photo ' + (i + 1) + ' of ' + n;
    }

    /* ---- size guide: the dialog for the piece or the sock; rows the product doesn't sell dim, the picked one is marked ---- */
    openGuide(which) {
      var d = this.querySelector('[data-lp-size-dialog="' + which + '"]'); if (!d || !d.showModal) return;
      var sizes = [], current = null, title = null, self = this;
      if (which === 'sock') {
        var sk = this.sock();
        if (sk) { sizes = sk.variants.map(function (v) { return v.size; }); current = this.addon.size; title = sk.title; }
      } else {
        var p = this.pieces[this.index];
        this.optionIndexes(p).forEach(function (i) { if (isSize(p.options[i].name)) { sizes = p.options[i].values; current = self.selected[p.options[i].name]; } });
        title = p.title;
      }
      var key = function (v) { return norm(shortSize(v)).replace(/\s+/g, ''); };
      var sold = sizes.map(key);
      slice.call(d.querySelectorAll('tr[data-size]')).forEach(function (tr) {
        var k = key(tr.dataset.size);
        tr.classList.toggle('is-off', sold.length > 0 && sold.indexOf(k) < 0);
        tr.classList.toggle('is-current', current != null && key(current) === k);
      });
      var h = d.querySelector('[data-lp-guide-title]'); if (h && title) h.textContent = title;
      d.showModal();
    }

    /* ---- order nudge + sock add-on ---- */
    watchCart() {
      var self = this, seq = 0;
      this.refreshCart = function () {
        var my = ++seq;
        fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (c) {
          if (my !== seq || !self.isConnected) return;
          self.cartSubtotal = c.original_total_price || c.total_price || 0;
          self.render();
        }).catch(function () {});
      };
      document.documentElement.addEventListener('cart:change', this.refreshCart);
      document.addEventListener('cart:refresh', this.refreshCart);
    }
    sock() { return this.socks[this.addon.style] || this.socks[0] || null; }
    packCount() { var b = this.querySelectorAll('[data-lp-addon-pack]')[this.addon.pack]; return b ? parseInt(b.dataset.count, 10) || 1 : 1; }
    /* keep colour and size picks the chosen sock can honour */
    settleSock() {
      var sk = this.sock(), a = this.addon; if (!sk) return;
      var colours = sk.variants.filter(function (v) { return v.available; }).map(function (v) { return v.colour; });
      if (colours.indexOf(a.colour) < 0) a.colour = colours[0] || (sk.variants[0] && sk.variants[0].colour);
      var sizes = sk.variants.filter(function (v) { return v.colour === a.colour && v.available; }).map(function (v) { return v.size; });
      if (sizes.indexOf(a.size) < 0) a.size = sizes[0] || (sk.variants[0] && sk.variants[0].size);
    }
    sockVariant() {
      var sk = this.sock(), a = this.addon; if (!sk) return null;
      return sk.variants.filter(function (v) { return v.colour === a.colour && v.size === a.size; })[0] || null;
    }
    tierPct(cents) { var hit = this.valueTiers.filter(function (t) { return cents >= t.spend; }).pop(); return hit ? hit.pct : 0; }
    nextTier(cents) { var cur = this.tierPct(cents); return this.valueTiers.filter(function (t) { return t.spend > cents && t.pct > cur; })[0] || null; }
    /* the card: how far cart + this piece is from the next tier; the socks that could close it; controls when on */
    renderAddon(p, shown, stock) {
      var el = this.addonEl; if (!el) return;
      var only = el.dataset.lpAddonOnly, sk = this.sock(), a = this.addon, self = this;
      var show = stock && !!shown && (!only || only === p.handle);
      el.hidden = !show; this.addonShown = show;
      if (!show) return;
      var base = this.cartSubtotal + this.qty * shown.price, basePct = this.tierPct(base), next = this.nextTier(base);
      var packs = slice.call(el.querySelectorAll('[data-lp-addon-pack]')), counts = packs.map(function (b) { return parseInt(b.dataset.count, 10) || 1; });
      var sv = this.sockVariant(), sockPrice = sv ? sv.price : (sk && sk.variants[0] ? sk.variants[0].price : 0);
      var reach = Math.max.apply(null, counts.concat([0])) * sockPrice;
      var count = counts[a.pack] || 1, on = a.on && sv && sv.available;
      this.addonExtra = on ? count * sv.price : 0;
      var banner = el.querySelector('[data-lp-addon-banner]'), title = el.querySelector('[data-lp-addon-title]'), text = el.querySelector('[data-lp-addon-text]');
      var fill = function (fmt, amount, pct) { return String(fmt || '').replace('{amount}', String(amount)).replace('{pct}', String(pct)); };
      var state = null;
      if (next && sk && next.spend - base <= reach) {
        var gap = next.spend - (base + (on ? count * sv.price : 0));
        state = gap <= 0 ? { key: 'unlocked', txt: fill(text.dataset.lpUnlocked, 0, next.pct) } : { key: 'locked', txt: fill(text.dataset.lpAdd, Math.ceil(gap / 100), next.pct) };
      } else if (basePct > 0) state = { key: 'applied', txt: fill(text.dataset.lpApplied, 0, basePct) };
      if (banner) {
        banner.hidden = !state;
        if (state) { banner.classList.toggle('is-unlocked', state.key !== 'locked'); title.textContent = title.dataset['lp' + state.key.charAt(0).toUpperCase() + state.key.slice(1)] || ''; text.textContent = state.txt; }
      }
      var row = el.querySelector('[data-lp-addon-row]');
      if (!sk) { if (row) row.hidden = true; el.hidden = !state; return; }
      row.hidden = false;
      el.classList.toggle('is-on', !!on);
      var tg = el.querySelector('[data-lp-addon-toggle]'), soldOut = !(sv && sv.available);
      if (tg) { tg.setAttribute('aria-checked', on ? 'true' : 'false'); tg.disabled = soldOut && !sk.variants.some(function (v) { return v.available; }); }
      var img = el.querySelector('[data-lp-addon-img]'), src = (sv && sv.image) || sk.image || '';
      if (img && img.getAttribute('src') !== src) { img.src = src; img.alt = sk.title; }
      /* the socks' price after the tier the order lands in with them added */
      var pct = this.tierPct(base + count * sockPrice), per = Math.round(sockPrice * (1 - pct / 100));
      var pe = el.querySelector('[data-lp-addon-price]'), ce = el.querySelector('[data-lp-addon-compare]');
      if (pe) pe.textContent = money(per, this.format, true);
      if (ce) { ce.hidden = per >= sockPrice; ce.textContent = money(sockPrice, this.format, true); }
      /* controls */
      var styles = el.querySelector('[data-lp-addon-styles]');
      if (styles && styles.childElementCount !== this.socks.length) {
        styles.innerHTML = this.socks.map(function (x, i) { return '<button type="button" class="lp-set-switcher__addon-tab" data-lp-addon-style="' + i + '" aria-pressed="false">' + esc(x.label || x.title) + '</button>'; }).join('');
        styles.hidden = this.socks.length < 2;
      }
      slice.call(el.querySelectorAll('[data-lp-addon-style]')).forEach(function (b, i) { b.setAttribute('aria-pressed', i === a.style ? 'true' : 'false'); });
      var tiles = el.querySelector('[data-lp-addon-colours]');
      if (tiles) {
        var seen = {}, cols = [];
        sk.variants.forEach(function (v) { if (v.colour && !seen[v.colour]) { seen[v.colour] = true; cols.push(v); } });
        tiles.innerHTML = cols.map(function (v) {
          var ok = sk.variants.some(function (w) { return w.colour === v.colour && w.available; });
          return '<button type="button" class="lp-set-switcher__addon-tile" data-lp-addon-colour="' + esc(v.colour) + '" aria-label="' + esc(v.colour) + '" aria-pressed="' + (v.colour === a.colour ? 'true' : 'false') + '"' + (ok ? '' : ' disabled') + '>' + (v.image || sk.image ? '<img src="' + esc(v.image || sk.image) + '" alt="" width="48" height="48" loading="lazy">' : '') + '</button>';
        }).join('');
        tiles.hidden = cols.length < 2;
      }
      var sizes = el.querySelector('[data-lp-addon-sizes]');
      if (sizes) {
        var sz = sk.variants.filter(function (v) { return v.colour === a.colour; });
        sizes.innerHTML = sz.map(function (v) { return '<button type="button" class="lp-set-switcher__pill" data-lp-addon-size="' + esc(v.size) + '" aria-pressed="' + (v.size === a.size ? 'true' : 'false') + '"' + (v.available ? '' : ' disabled') + '>' + esc(shortSize(v.size)) + '</button>'; }).join('');
        sizes.hidden = sz.length < 2 || !sz[0].size;
      }
      packs.forEach(function (b, i) {
        b.setAttribute('aria-pressed', i === a.pack ? 'true' : 'false');
        var n = counts[i], pp = self.tierPct(base + n * sockPrice), each = Math.round(sockPrice * (1 - pp / 100)), pr = b.querySelector('[data-lp-addon-pack-price]');
        if (pr) pr.textContent = money(each, self.format, true) + (n > 1 ? ' each' : '');
      });
    }

    /* ---- cart ---- */
    add() {
      var v = this.variant(), btn = this.querySelector('[data-lp-atc]'), err = this.querySelector('[data-lp-error]');
      if (!v) { this.flagMissing(); return; }
      if (!v.available || (btn && btn.getAttribute('aria-busy') === 'true')) return;
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      var sections = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } }));
      if (btn) btn.setAttribute('aria-busy', 'true');
      var self = this, qty = this.qty;
      var items = [{ id: v.id, quantity: qty }], sv = this.sockVariant();
      if (this.addonShown && this.addon.on && sv && sv.available) items.push({ id: sv.id, quantity: this.packCount() });
      var fail = function (msg) { if (btn) btn.removeAttribute('aria-busy'); if (err) { err.hidden = false; err.textContent = msg || 'Could not add to cart. Please try again.'; } };
      fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items: items, sections: sections.join(','), sections_url: root + 'variants/' + v.id }) })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          if (!res.ok) { fail(res.json && res.json.description); return; }
          return fetch(root + 'cart.js').then(function (r) { return r.json(); }).then(function (cart) {
            if (btn) btn.removeAttribute('aria-busy');
            cart.sections = res.json.sections;
            if (!cart.sections) document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            self.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: res.json.items || [res.json], cart: cart } }));
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } }));
            if (window.LP) window.LP.emit('cart-add', { items: items, cart: cart, source: 'lp-set-switcher' });
          });
        })
        .catch(function () { fail(); });
    }

    /* ---- on screen: the idle float runs, and a colour picked elsewhere sweeps in ---- */
    watchScreen() {
      if (!('IntersectionObserver' in window)) { this.inView = true; return; }
      var self = this;
      this.screenIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          self.classList.toggle('is-live', e.isIntersecting);
          self.inView = e.isIntersecting && e.intersectionRatio >= 0.2;
          if (self.inView && self.deferred) self.paint({});
          if (self.inView && !self.warmed) { self.warmed = true; self.preloadAround(); }
        });
      }, { threshold: [0, 0.2, 0.4] });
      this.screenIO.observe(this);
    }
    /* ---- analytics ---- */
    watchView() {
      if (!('IntersectionObserver' in window)) return;
      var self = this;
      this.io = new IntersectionObserver(function (entries) {
        if (!entries.some(function (e) { return e.isIntersecting; })) return;
        self.io.disconnect(); self.io = null;
        var listId = self.dataset.gaListId || '', listName = self.dataset.gaListName || '';
        var items = self.pieces.map(function (p, i) { return { item_id: p.ga_id, item_name: p.title, price: self.minPrice(p, self.colour) / 100, index: i + 1, item_list_id: listId, item_list_name: listName }; });
        gaPush('ee_view_item_list', { items: items }, listId, listName);
      }, { threshold: 0.2 });
      this.io.observe(this);
    }
  }
  window.customElements.define('lp-set-switcher', LpSetSwitcher);
})();
